/**
 * catalog.ts — категории, загрузка дерева, серверная пагинация/фильтрация/поиск
 * Проект: TorgiRT (shared — все витрины)
 *
 * Архитектура v2 (серверная пагинация):
 * - Дерево каталога (категории + подкатегории + productCount) загружается один раз
 * - Товары загружаются постранично с сервера через fetchProducts()
 * - Вся фильтрация, поиск и сортировка выполняются на сервере (SQL)
 * - state.products всегда содержит ровно одну страницу товаров
 * - state.pagination хранит { total, page, perPage, totalPages }
 */

import type { Product, Subcategory } from './types';
import {
  state, ITEMS_PER_PAGE, productLookup, filterCodeToName,
} from './state';
import {
  normalizeProduct, getConfig,
} from './helpers';
import { getUrlCatDbId } from './url';
import { getFavorites } from './favorites';

/* ── Серверный запрос товаров ── */

let _fetchController: AbortController | null = null;
let _fetchGeneration = 0;

export type FetchResult = 'ok' | 'stale' | 'error';

interface FetchOverrides {
  category?: number | null;
  page?: number;
  sort?: string;
  q?: string;
  filters?: Record<string, string>;
  price_min?: string;
  price_max?: string;
  stock_only?: boolean;
  favorites?: string;
}

/**
 * Query-параметры каталога (фильтры SPA) для API и export.
 */
export function buildCatalogApiParams(
  overrides: FetchOverrides = {},
  options: { includePagination?: boolean } = {},
): URLSearchParams {
  const includePagination = options.includePagination !== false;
  const params = new URLSearchParams();

  const catDbId = overrides.category ?? getUrlCatDbId();
  if (catDbId != null && catDbId !== 0) {
    params.set("category", String(catDbId));
  }

  if (includePagination) {
    const page = overrides.page ?? state.currentPage;
    if (page > 1) {
      params.set("page", String(page));
    }
    params.set("per_page", String(ITEMS_PER_PAGE));
  }

  const q = overrides.q ?? state.searchQuery;
  if (q && q.trim()) {
    params.set("q", q.trim());
  }

  const sort = overrides.sort ?? state.sortBy;
  if (sort) {
    params.set("sort", sort);
  }

  const filters = overrides.filters ?? state.activeFilters;
  for (const [code, value] of Object.entries(filters)) {
    if (value) {
      params.set("filters[" + code + "]", value);
    }
  }

  const priceMin = overrides.price_min ?? state.priceMin;
  const priceMax = overrides.price_max ?? state.priceMax;
  if (priceMin) {
    params.set("price_min", priceMin);
  }
  if (priceMax) {
    params.set("price_max", priceMax);
  }

  const stockOnly = overrides.stock_only ?? state.stockOnly;
  if (stockOnly) {
    params.set("stock_only", "1");
  }

  const favorites = overrides.favorites ?? (state.favoritesOnly ? getFavorites().join(",") : "");
  if (favorites) {
    params.set("favorites", favorites);
  }

  return params;
}

/**
 * Загрузить страницу товаров с сервера.
 */
export async function fetchProducts(overrides: FetchOverrides = {}): Promise<FetchResult> {
  _fetchController?.abort();
  _fetchController = new AbortController();
  const { signal } = _fetchController;
  const generation = ++_fetchGeneration;
  const hadProducts = state.products.length > 0;

  const productsUrl = getConfig("productsApiUrl", "/i/ajax/catalog/products") as string;

  const favorites = overrides.favorites ?? (state.favoritesOnly ? getFavorites().join(",") : "");
  if (state.favoritesOnly && !favorites) {
    state.products = [];
    state.pagination = { total: 0, page: 1, perPage: ITEMS_PER_PAGE, totalPages: 1 };
    state.filterValues = {};
    state.productPage = null;
    state.loadError = false;
    return 'ok';
  }

  const params = buildCatalogApiParams(overrides, { includePagination: true });

  const qs = params.toString();
  const url = qs ? productsUrl + "?" + qs : productsUrl;

  state.loadError = false;

  try {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data = await response.json();

    if (generation !== _fetchGeneration) {
      return 'stale';
    }

    state.productPage = null;

    state.products = (data.products || []).map(normalizeProduct);

    state.pagination = {
      total:      data.total ?? 0,
      page:       data.page ?? 1,
      perPage:    data.per_page ?? ITEMS_PER_PAGE,
      totalPages: data.total_pages ?? 1,
    };

    state.currentPage = state.pagination.page;

    state.filterValues = data.filter_values ?? {};

    for (const product of state.products) {
      productLookup.set(product.article, product);
    }

    return 'ok';

  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') {
      return 'stale';
    }
    if (generation !== _fetchGeneration) {
      return 'stale';
    }
    console.warn("[catalog.ts] Не удалось загрузить товары:", err);
    if (hadProducts) {
      state.loadError = false;
      return 'error';
    }
    state.loadError = true;
    state.products = [];
    state.pagination = { total: 0, page: 1, perPage: ITEMS_PER_PAGE, totalPages: 1 };
    state.filterValues = {};
    return 'error';
  }
}

/* ── Обёртки для обратной совместимости ── */

export function getFilteredProducts(): Product[] {
  return state.products;
}

export function getPaginatedProducts(): Product[] {
  return state.products;
}

export function getTotalPages(): number {
  return state.pagination?.totalPages ?? 1;
}

export function getSelectedSubcategory(): Subcategory | null {
  if (!state.selectedSubcategory) {
    return null;
  }

  for (const category of state.catalogData.categories) {
    const sub = category.subcategories.find(
      (subcategory) => subcategory.id === state.selectedSubcategory,
    );
    if (sub) {
      return sub;
    }
  }

  return null;
}

/* ── Category selection (async — подгрузка с сервера) ── */

export async function selectCategory(categoryId: string): Promise<FetchResult> {
  const category = state.catalogData.categories.find(
    (candidate) => candidate.id === categoryId,
  );

  if (!category) {
    return 'ok';
  }

  state.selectedCategory = categoryId as any;
  state.selectedSubcategory = null;
  state.breadcrumbPath = {
    categoryName: category.name,
    subcategoryName: "",
  } as any;
  state.currentFilters = [...(category.filters as any)];
  state.activeFilters = {};
  state.searchMode = "strict";
  state.currentPage = 1;
  state.catalogOpen = false;

  return fetchProducts({ category: category.dbId, page: 1 });
}

export async function selectSubcategory(categoryId: string, subcategoryId: string): Promise<FetchResult> {
  const category = state.catalogData.categories.find(
    (candidate) => candidate.id === categoryId,
  );
  const subcategory = category?.subcategories.find(
    (candidate) => candidate.id === subcategoryId,
  );

  if (!category || !subcategory) {
    return 'ok';
  }

  state.selectedCategory = categoryId as any;
  state.selectedSubcategory = subcategory.id as any;
  state.breadcrumbPath = {
    categoryName: category.name,
    subcategoryName: subcategory.name,
  } as any;
  state.currentFilters = [...(category.filters as any)];
  state.activeFilters = {};
  state.searchMode = "strict";
  state.currentPage = 1;
  state.catalogOpen = false;

  return fetchProducts({ category: subcategory.dbId, page: 1 });
}

/* ── Product lookup ── */

export function registerProductLookup(): void {
  productLookup.clear();
  filterCodeToName.clear();

  for (const category of state.catalogData.categories) {
    if (Array.isArray(category.filters)) {
      for (const f of category.filters as any[]) {
        if (typeof f === "object" && f.code && f.name) {
          filterCodeToName.set(f.code, f.name);
        }
      }
    }
  }
}

/* ── Загрузка дерева каталога из PHP API ── */

export async function loadCatalogData(): Promise<void> {
  if (window.__CATALOG_TREE__ && Array.isArray(window.__CATALOG_TREE__.categories)) {
    state.catalogData = window.__CATALOG_TREE__;
    delete (window as any).__CATALOG_TREE__;
    return;
  }

  if ((window as any).__CATALOG__ && Array.isArray((window as any).__CATALOG__.categories)) {
    const { unpackCatalog } = await import('./helpers');
    const full = unpackCatalog((window as any).__CATALOG__);
    for (const cat of full.categories) {
      for (const sub of cat.subcategories) {
        (sub as any).productCount = ((sub as any).products || []).length;
        delete (sub as any).products;
      }
    }
    state.catalogData = full;
    delete (window as any).__CATALOG__;
    return;
  }

  const treeUrl = getConfig("catalogTreeUrl", "/i/ajax/catalog/tree") as string;

  try {
    const response = await fetch(treeUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data = await response.json();
    if (Array.isArray(data.categories)) {
      state.catalogData = data;
      return;
    }
  } catch (err) {
    console.warn("[catalog.ts] Не удалось загрузить дерево каталога:", err);
  }

  state.catalogData = { categories: [] };
}

/* ── Fallback / suggestions ── */

export function countFallbackResults(_query: string): { globalCount: number; partialCount: number; suggestion: null } {
  return { globalCount: 0, partialCount: 0, suggestion: null };
}
